import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import { KeyboardArrowLeft, KeyboardArrowRight, PhoneCallback } from "@mui/icons-material";
import { CircularProgress, Chip, Breadcrumbs, Link } from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DebounceInput from "../../domain/DebounceInput";
import { useCall } from "../../lib/call-context";
import ApiService from "../../axios/Bookings-Axios";
import { toast, ToastContainer } from "react-toastify";
import RescheduleDrawer from "../modals/RescheduleDrawer";

interface Row {
    id: string,
    booked_in: string,
    created_at: string,
    readable_services: string,
    expanded_row: RowExpanded,
    total_cost: number
}

interface RowExpanded {
    full_name: string,
    street_address: string,
    postcode: string,
    phone: string,
    notes: string,
    house_type: string,
    id: string,
    email: string,
}

function formatDate(old_date: string | Date): string {
    const date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: 'numeric', minute: 'numeric'
        });
    }
    return 'No Valid Date';
}

function Row(props: { row: Row; initialOpen?: boolean, onRowClick: (rowId: number) => void }) {
    const { row } = props;
    const [open, setOpen] = useState(props.initialOpen || false);
    const { makeCall } = useCall();

    const handleClick = () => props.onRowClick(Number(row.id));

    const handleCallCustomer = async () => {
        if (row.expanded_row.phone) {
            makeCall(row.expanded_row.phone, row.expanded_row.full_name);
        } else {
            toast.error("Phone number not available", { containerId: "needs-rescheduling-table" });
        }
    };

    return (
        <React.Fragment>
            <tr>
                <td><IconButton aria-label="expand row" variant="plain" color="neutral" size="sm" onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></td>
                <td>{formatDate(row.created_at)}</td>
                <td>{formatDate(row.booked_in)}</td>
                <td>{row.expanded_row.full_name}</td>
                <td>{row.readable_services}</td>
                <td>£{row.total_cost}</td>
                <td>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button sx={{ textWrap: 'nowrap' }} size="sm" color="primary" onClick={handleClick}>Reschedule</Button>
                        <IconButton variant="solid" sx={{ ml: 1 }} size="sm" color="primary" onClick={handleCallCustomer}><PhoneCallback /></IconButton>
                    </Box>
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={7}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Typography level="body-lg" component="div">Customer Information</Typography>
                            <Table borderAxis="bothBetween" aria-label="customer details" sx={{ '& > thead > tr > th:nth-child(n + 3), & > tbody > tr > td:nth-child(n + 3)': { textAlign: 'right' }, '--TableCell-paddingX': '0.5rem' }}>
                                <thead><tr><th>Full Name</th><th>Number</th><th>Email</th><th>Street Address</th><th>Postcode</th></tr></thead>
                                <tbody><tr><td>{row.expanded_row.full_name}</td><td>{row.expanded_row.phone}</td><td>{row.expanded_row.email}</td><td>{row.expanded_row.street_address}</td><td>{row.expanded_row.postcode}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={7}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Table borderAxis="bothBetween" aria-label="notes">
                                <thead><tr><th>Notes</th></tr></thead>
                                <tbody><tr><td style={{ whiteSpace: 'pre-wrap' }}>{row.expanded_row.notes || 'No notes'}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
        </React.Fragment>
    );
}

const mapRawDataToRow = (data: any[]): Row[] => {
    return data.map(item => {
        return {
            id: item.id.toString(),
            booked_in: item.booked_in,
            created_at: item.created_at,
            total_cost: item.total_cost,
            readable_services: item.readable_services,
            expanded_row: {
                full_name: item.customer?.full_name || '',
                street_address: item.customer?.street_address || '',
                postcode: item.customer?.postcode || '',
                phone: item.customer?.phone || '',
                notes: item.customer?.notes || '',
                house_type: item.customer?.house_type || '',
                id: item.customer?.id?.toString() || '',
                email: item.customer?.email || '',
            },
        };
    });
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

export default function NeedsRescheduling() {
    const [loadedData, setLoadingData] = useState(false);
    const [rows, setRows] = useState<Row[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    const [openBookingId, setOpenBookingId] = useState(0);
    const [openRescheduleModal, setOpenRescheduleModal] = useState(false);

    const fetchData = useCallback(async () => {
        setLoadingData(false);
        try {
            const bookingsData = await ApiService.getNeedsReschedulingBookings({ page: page + 1, search });
            setRows(mapRawDataToRow(bookingsData.results || []));
            setPageCount(bookingsData.count || 0);
        } catch (error) {
            console.error("Failed to fetch needs rescheduling bookings:", error);
            setRows([]);
            setPageCount(0);
        } finally {
            setLoadingData(true);
        }
    }, [page, search]);

    const handleCallback = (refreshData?: boolean) => {
        setOpenRescheduleModal(false);
        if (refreshData) {
            fetchData();
        }
    };

    const handleOpenRescheduleModal = (rowId: number) => {
        setOpenRescheduleModal(true);
        setOpenBookingId(rowId);
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDebounce = (value: string) => {
        setPage(0);
        setSearch(value);
    };

    const getLabelDisplayedRowsTo = () => {
        return Math.min(pageCount, (page + 1) * rowsPerPage);
    };

    return (
        <>
            <ToastContainer containerId={"needs-rescheduling-table"} position="top-right" autoClose={5000} theme="dark" />

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0 }}>
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon />
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Needs Rescheduling
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{
                display: 'flex',
                mb: 1,
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'start', sm: 'center' },
                flexWrap: 'wrap',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography level="h2" component="h1">
                        Needs Rescheduling
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <DebounceInput sx={{ width: { xs: '100%', sm: '250px' } }} startDecorator={<SearchRoundedIcon />}
                               placeholder="Search by any field..."
                               debounceTimeout={1000}
                               handleDebounce={handleDebounce}
                />
            </Box>

            <Sheet sx={{ 
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
            }}>
                <Table hoverRow aria-label="collapsible table" sx={{ minWidth: 800 }}>
                    <thead>
                    <tr>
                        <th style={{ width: 40 }} aria-label="empty" />
                        <th style={{ width: '15%' }}>Created At</th>
                        <th style={{ width: '15%' }}>Time Booked In</th>
                        <th>Customer Name</th>
                        <th>Services</th>
                        <th>Total Cost</th>
                        <th aria-label="last" />
                    </tr>
                    </thead>
                    <tbody>
                    {loadedData ? (
                        rows.length > 0 ? (
                            rows.map((row) => (
                                <Row
                                    key={row.id}
                                    row={row}
                                    initialOpen={search.length > 0}
                                    onRowClick={handleOpenRescheduleModal}
                                />
                            ))
                        ) : (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>No bookings need rescheduling</td></tr>
                        )
                    ) : (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                    )}
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colSpan={7}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                    {labelDisplayedRows({
                                        from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                        to: getLabelDisplayedRowsTo(),
                                        count: pageCount,
                                    })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowLeft /></IconButton>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowRight /></IconButton>
                                </Box>
                            </Box>
                        </td>
                    </tr>
                    </tfoot>
                </Table>
            </Sheet>
            <React.Fragment>
                <RescheduleDrawer 
                    key={'reschedule-modal-' + openRescheduleModal} 
                    open={openRescheduleModal}
                    bookingId={openBookingId} 
                    onClose={handleCallback} 
                />
            </React.Fragment>
        </>
    );
}
